import { useState } from 'react';
import { BUTTON_VARIANTS } from 'ui/button';
import { Edit16 } from 'ui/icons/action/Edit16';
import { BottomTooltip } from 'ui/tooltip/Tooltip';
import { useKeyPress } from 'utils/hooks/useKeyPress';
import { useIsOverflowing } from 'utils/hooks/useIsOverflowing';
import {
  InputContainer,
  NameDisplay,
  NameDisplayPlaceholder,
  StyledButton,
  StyledFauxButton,
  StyledInput,
} from './InlineEdit.styled';
import { EDIT_INPUT_TEST_ID, EDIT_BUTTON_TEST_ID, FAUX_EDIT_BUTTON_TEST_ID } from './constants';
import type { InlineEditProps, FauxInlineEditProps } from './types';
import { CharacterCountTooltip } from './CharacterCountTooltip';

export const InlineEdit = ({
  hasError,
  isRequired = false,
  maxLength,
  onBlur,
  onChange,
  onReset,
  placeholder = '',
  showTooltipWhenOverflow = false,
  value = '',
  id = 'uiInlineEdit',
  variant = 'default',
}: InlineEditProps) => {
  const [isEditMode, setIsEditMode] = useState(false);

  const handleBlur = () => {
    if (!value?.trim() && isRequired) {
      onReset?.();
    } else {
      onBlur?.();
    }
    setIsEditMode(false);
  };

  useKeyPress('Enter', handleBlur);
  useKeyPress('Escape', handleBlur);

  const { elementRef, isOverflowing } = useIsOverflowing<HTMLSpanElement>();
  const isTooltipEnabled =
    !!maxLength && value.length >= maxLength - 10 && value.length <= maxLength;
  const tooltipContent = `${value.length}/${maxLength}`;

  return isEditMode ? (
    <InputContainer data-input-type={variant}>
      <CharacterCountTooltip tooltipContent={tooltipContent} isTooltipEnabled={isTooltipEnabled}>
        <StyledInput
          autoFocus
          data-input-type={variant}
          data-test={EDIT_INPUT_TEST_ID}
          hasError={hasError}
          id={id}
          autoComplete={isTooltipEnabled ? 'off' : 'on'}
          isRequired={isRequired}
          maxLength={maxLength}
          onBlur={handleBlur}
          onChange={onChange}
          placeholder={placeholder}
          value={value}
        />
      </CharacterCountTooltip>
    </InputContainer>
  ) : (
    <BottomTooltip
      isEnabled={isOverflowing && showTooltipWhenOverflow}
      isUsedOnButton
      tooltipContent={value}
    >
      <StyledButton
        data-input-type={variant}
        data-test={EDIT_BUTTON_TEST_ID}
        onClick={() => setIsEditMode(true)}
        variant={BUTTON_VARIANTS.GHOST}
      >
        <NameDisplay ref={elementRef} data-input-type={variant}>
          {value || <NameDisplayPlaceholder>{placeholder}</NameDisplayPlaceholder>}
        </NameDisplay>
        <Edit16 />
      </StyledButton>
    </BottomTooltip>
  );
};

export const FauxInlineEdit = ({
  showTooltipWhenOverflow = false,
  value = '',
  variant = 'default',
}: FauxInlineEditProps) => {
  const { elementRef, isOverflowing } = useIsOverflowing<HTMLSpanElement>();

  return (
    <BottomTooltip
      isEnabled={isOverflowing && showTooltipWhenOverflow}
      isUsedOnButton
      tooltipContent={value}
    >
      <StyledFauxButton
        data-input-type={variant}
        data-test={FAUX_EDIT_BUTTON_TEST_ID}
        variant={BUTTON_VARIANTS.GHOST}
      >
        <NameDisplay ref={elementRef} data-input-type={variant}>
          {value}
        </NameDisplay>
      </StyledFauxButton>
    </BottomTooltip>
  );
};
